import { EventCalendar } from '@/types/Calendar.ts'
import { Daycare } from '@/types/Daycare.ts'
import { StaffWithLinks } from '@/types/Staff.ts'
import { Work } from '@/types/work.ts'
import { Fragment, FunctionComponent, useState } from 'react'
import moment from 'moment-timezone'
import { TableCell } from '@mui/material'
import styles from './staffWorkDayCell.module.scss'
import ButtonIcon from '@/components/_custom/Buttons/ButtonIcon/ButtonIcon.tsx'
import { Add } from '@mui/icons-material'
import EditOrCreateWorkDialog from '../../../Dialog/staffWork/editOrCreateStaffWork/editOrCreateWorkDialog.tsx'
import ShowStaffWorkDialog from '../../../Dialog/showEvent/showStaffWork/showStaffWorkDialog.tsx'
import {
    getPreparationTimeAfterRange,
    getPreparationTimeBeforeRange,
    TimeRange
} from '@/modules/helper/workPreparationTimeHelper'
import { defineWorkTypeStyle } from '@/container/planning/utils/eventsWorks'
import { isWithinEditionLimit } from '@/modules/helper/editionLimitHelper'

interface StaffWorkDayCellProps {
    events: EventCalendar[]
    isOpen: boolean
    columnWidth: string
    canWrite: boolean
    daycare: Daycare
    staffsListData: Array<StaffWithLinks>
    date: string
    from: string
    to: string
    works: Array<Work>
    staffId: string
    refetchData: () => void
}

const StaffWorkDayCell: FunctionComponent<StaffWorkDayCellProps> = ({
    events,
    isOpen,
    columnWidth,
    canWrite,
    daycare,
    staffsListData,
    date,
    from,
    to,
    works,
    staffId,
    refetchData
}: StaffWorkDayCellProps) => {
    const [isOpenEditOrCreateWorkDialog, setIsOpenEditOrCreateWorkDialog] = useState<boolean>(false)
    const [isOpenShowWorkDialog, setIsOpenShowWorkDialog] = useState<boolean>(false)

    const [fromEdit, setFromEdit] = useState<string>(from)
    const [toEdit, setToEdit] = useState<string>(to)

    const [selectedWork, setSelectedWork] = useState<Work | undefined>(undefined)

    const canEdit: boolean = canWrite && isWithinEditionLimit(date)

    if (!isOpen) return null

    function openEditOrCreateWork(from: string, to: string): void {
        setFromEdit(from)
        setToEdit(to)
        setIsOpenEditOrCreateWorkDialog(true)
    }

    const canAddBefore: boolean =
        events.length > 0 ? moment(events[0].start).format('HHmm') > moment(from, 'HH:mm').format('HHmm') : false
    const canAddAfter: boolean =
        events.length > 0
            ? moment(events[events.length - 1].end).format('HHmm') < moment(to, 'HH:mm').format('HHmm')
            : false

    return (
        <>
            <TableCell align='center' sx={{ width: columnWidth }}>
                <div className={styles.WorkCell}>
                    {canAddBefore && canEdit && (
                        <ButtonIcon
                            icon={<Add />}
                            className={styles.ButtonIcon}
                            onClick={() => openEditOrCreateWork(from, moment(events[0].start).format('HH:mm'))}
                        />
                    )}
                    {events.length === 0 ? (
                        canEdit ? (
                            <ButtonIcon
                                icon={<Add />}
                                className={styles.ButtonIcon}
                                onClick={() => openEditOrCreateWork(from, to)}
                            />
                        ) : null
                    ) : (
                        events.map((event, index) => {
                            const work: Work = event.slots as Work

                            const preparationBeforeRange: TimeRange | null = work.preparationTimeBefore
                                ? getPreparationTimeBeforeRange(event.start, work.preparationTimeBefore)
                                : null
                            const preparationAfterRange: TimeRange | null = work.preparationTimeAfter
                                ? getPreparationTimeAfterRange(event.end, work.preparationTimeAfter)
                                : null

                            return (
                                <Fragment key={index}>
                                    {preparationBeforeRange && (
                                        <span className={styles.PreparationHours}>{preparationBeforeRange.start}</span>
                                    )}
                                    <a
                                        className={`${styles.WorkHour}
                                        ${defineWorkTypeStyle(event.type)}
                                        ${preparationBeforeRange ? styles.HasPreparationBefore : ''}
                                        ${preparationAfterRange ? styles.HasPreparationAfter : ''}
                                    `}
                                        onClick={() => {
                                            setSelectedWork(work)
                                            setIsOpenShowWorkDialog(true)
                                        }}
                                    >
                                        <span className={styles.Hours}>
                                            {moment(event.start).format('HH:mm')} - {moment(event.end).format('HH:mm')}
                                        </span>
                                        <span className={styles.GroupName}>{event.title}</span>
                                    </a>
                                    {preparationAfterRange && (
                                        <span className={styles.PreparationHours}>{preparationAfterRange.end}</span>
                                    )}
                                </Fragment>
                            )
                        })
                    )}
                    {canAddAfter && canEdit && (
                        <ButtonIcon
                            icon={<Add />}
                            className={styles.ButtonIcon}
                            onClick={() =>
                                openEditOrCreateWork(moment(events[events.length - 1].end).format('HH:mm'), to)
                            }
                        />
                    )}
                </div>
            </TableCell>
            {isOpenEditOrCreateWorkDialog && canEdit && (
                <EditOrCreateWorkDialog
                    open={isOpenEditOrCreateWorkDialog}
                    handleClose={() => {
                        setIsOpenEditOrCreateWorkDialog(false)
                        setSelectedWork(undefined)
                    }}
                    dateReadonly={false}
                    work={selectedWork}
                    daycare={daycare}
                    staffsListData={staffsListData}
                    date={date}
                    from={fromEdit}
                    to={toEdit}
                    works={works}
                    refetch={refetchData}
                    selectedStaffId={staffId}
                />
            )}
            {isOpenShowWorkDialog && selectedWork && (
                <ShowStaffWorkDialog
                    open={isOpenShowWorkDialog}
                    handleClose={() => {
                        setIsOpenShowWorkDialog(false)
                        setSelectedWork(undefined)
                    }}
                    staffWork={selectedWork}
                    daycare={daycare}
                    works={works}
                    staffsListData={staffsListData}
                    refetchData={refetchData}
                    canEditStaffWorks={canEdit}
                />
            )}
        </>
    )
}

export default StaffWorkDayCell
